import { gql } from 'graphql-request';

export const ACTIVITY_FIELDS = gql`
  fragment ActivityFields on ActivityDto {
    activityId
    type
    name
    time
    description
    newStatus
    newPercentage
    createdAt
    attemptAt
    userId
    taskId
    noteId
    statusHistoryId
    statusHistory {
      statusHistoryId
      previousStatus
      newStatus
      reason
      createdAt
      user {
        firstName
        lastName
      }
      task {
        taskId
        name
      }
    }
    note {
      noteId
      name
      description
      documents {
        documentId
        name
        url
      }
    }
    documents {
      documentId
      name
      url
    }
    organizationId
    organization {
      name
    }
    task {
      name
      taskType
      parentTaskId
    }
    user {
      firstName
      lastName
      avatar
    }
    project {
      name
      projectId
      address
      lat
      long
      client {
        email
      }
    }
    punchClockId
    punchClock {
      punchClockId
      userId
      projectId
      organizationId
      action
      time
      createdAt
      geofenceId
      geofence {
        geofenceId
        name
        color
        isActive
        projectId
        warehouseId
        category {
          geofenceCategoryId
          name
          icon
          color
        }
      }
      user {
        firstName
        lastName
        avatar
      }
      project {
        name
        projectId
        address
        lat
        long
      }
      organization {
        name
      }
    }
    incidentId
    incident {
      incidentId
      name
      description
      incidentTime
      delay
      delayUnitOfMeasure
      incidentType
      otherIncidentType
      status
      acknowledgedBy
      acknowledgedAt
      acknowledgedByUser {
        firstName
        lastName
        avatar
      }
      user {
        firstName
        lastName
        avatar
      }
      project {
        name
        projectId
      }
      tasks {
        taskId
        name
      }
      documents {
        documentId
        name
        url
      }
    }
    budgetRequestId
    budgetRequest {
      budgetRequestId
      description
      requestedStartDate
      requestedEndDate
      maxBudgetAmount
      status
      task {
        taskId
        name
        project {
          projectId
          name
        }
      }
      originatorOrganization {
        organizationId
        name
      }
      createdByUser {
        firstName
        lastName
        avatar
      }
      recipients {
        budgetRequestId
        recipientOrganizationId
        status
        recipientOrganization {
          organizationId
          name
        }
      }
    }
    budgetProposalId
    budgetProposal {
      budgetProposalId
      proposedAmount
      proposedStartDate
      proposedEndDate
      status
      isCounterProposal
      proposingOrganization {
        organizationId
        name
      }
      createdByUser {
        firstName
        lastName
        avatar
      }
    }
    resolutionStatus
    creationTrigger
    pendingItemsSnapshot
  }
`;

export const getActivitiesByTaskId = gql`
  query getActivitiesByTaskId(
    $projectId: String!
    $taskId: String!
    $page: Float
    $limit: Float
  ) {
    getActivitiesByTaskId(
      projectId: $projectId
      taskId: $taskId
      page: $page
      limit: $limit
    ) {
      total
      items {
        ...ActivityFields
      }
    }
  }
  ${ACTIVITY_FIELDS}
`;

export const getActivityByTaskIdWithIncidents = gql`
  query getActivityByTaskIdWithIncidents(
    $projectId: String!
    $taskId: String!
    $page: Float = 1
    $limit: Float = 5
  ) {
    getActivityByTaskIdWithIncidents(
      projectId: $projectId
      taskId: $taskId
      page: $page
      limit: $limit
    ) {
      total
      items {
        ...ActivityFields
      }
    }
  }
  ${ACTIVITY_FIELDS}
`;

export const getActivities = gql`
  query getActivities($projectId: String!, $page: Float, $limit: Float) {
    getActivities(projectId: $projectId, page: $page, limit: $limit) {
      total
      items {
        ...ActivityFields
      }
    }
  }
  ${ACTIVITY_FIELDS}
`;

export const getActivitiesByProjects = gql`
  query getActivitiesByProjects(
    $projectsId: [String!]!
    $page: Float
    $limit: Float
    $types: [String!]
    $after: String
    $resolutionStatus: String
  ) {
    getActivitiesByProjects(
      projectsId: $projectsId
      page: $page
      limit: $limit
      types: $types
      after: $after
      resolutionStatus: $resolutionStatus
    ) {
      total
      nextCursor
      hasMore
      items {
        ...ActivityFields
      }
    }
  }
  ${ACTIVITY_FIELDS}
`;

export const getActivitiesByOrganizationId = gql`
  query getActivitiesByOrganizationId(
    $organizationId: String!
    $page: Float
    $limit: Float
    $after: String
  ) {
    getActivitiesByOrganizationId(
      organizationId: $organizationId
      page: $page
      limit: $limit
      after: $after
    ) {
      total
      nextCursor
      hasMore
      items {
        ...ActivityFields
      }
    }
  }
  ${ACTIVITY_FIELDS}
`;

export const getActivitiesByOrganizationIdAndProjects = gql`
  query getActivitiesByOrganizationIdAndProjects(
    $organizationId: String!
    $projectIds: [String!]!
    $page: Float = 1
    $limit: Float = 5
    $after: String
  ) {
    getActivitiesByOrganizationIdAndProjects(
      organizationId: $organizationId
      projectIds: $projectIds
      page: $page
      limit: $limit
      after: $after
    ) {
      total
      nextCursor
      hasMore
      items {
        ...ActivityFields
      }
    }
  }
  ${ACTIVITY_FIELDS}
`;

export const getPendingInvoicesCountByProjects = gql`
  query getPendingInvoicesCountByProjects($projectsId: [String!]!) {
    getPendingInvoicesCountByProjects(projectsId: $projectsId)
  }
`;

export const updateActivity = gql`
  mutation updateActivity($activity: ActivityUpdateInput!) {
    updateActivity(activity: $activity) {
      ...ActivityFields
    }
  }
  ${ACTIVITY_FIELDS}
`;

export const deleteActivity = gql`
  mutation deleteActivity($id: String!) {
    deleteActivity(id: $id) {
      affected
      messages
    }
  }
`;

const ActivitiesGraphQL = {
  queries: {
    getActivities,
    getActivitiesByOrganizationId,
    getActivitiesByOrganizationIdAndProjects,
    getActivitiesByProjects,
    getActivitiesByTaskId,
    getActivityByTaskIdWithIncidents,
    getPendingInvoicesCountByProjects,
  },
  mutations: {
    updateActivity,
    deleteActivity,
  },
};

export default ActivitiesGraphQL;
